import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../modules/core/services/auth.service';

const UNAUTHORIZED = 401;

/**
 * Recovers from a 401 by getting a fresh token and replaying the request once.
 *
 * Before this existed nothing in the app reacted to a 401. A token that died mid-session
 * kept being sent on every subsequent call, and iframed there was no way to renew it at
 * all, so the only recovery was for the user to navigate away and back and let the iframe
 * remount. This closes that loop.
 *
 * Must stay the FIRST entry in httpInterceptorProviders. Being outermost is what makes the
 * replay work: the request arriving here has no Authorization header yet, so re-handling it
 * sends it back through AuthorizationInterceptor, which reads the token we just fetched.
 * Registered anywhere else, the replay would carry the same rejected token.
 */
@Injectable()
export class UnauthorizedInterceptor implements HttpInterceptor {

  constructor(private authService: AuthService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // The identity endpoints are how a token gets issued in the first place. Trying to
    // refresh in response to one of them failing would be circular.
    if (req.url.includes('/connect/')) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== UNAUTHORIZED) {
          return throwError(() => error);
        }

        // No token means this is not an expiry. Guest and pre-boot requests can legitimately
        // come back 401, and asking the parent to renew for those would fire a silent renew
        // on every anonymous page.
        if (!this.authService.accessToken) {
          return throwError(() => error);
        }

        return from(this.authService.refreshAccessToken()).pipe(
          switchMap(token => {
            if (!token) {
              // Renew failed. Surface the original 401 rather than a refresh error, so
              // callers still see the failure they were expecting.
              return throwError(() => error);
            }
            // Replayed through the inner chain only, so this catchError does not wrap it.
            // A second 401 propagates to the caller instead of looping.
            return next.handle(req);
          })
        );
      })
    );
  }
}
